import { z } from "zod";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useCreateProject } from "@/api/studio/projects/queries";
import { schema } from "@/api/studio/projects/types/project.type";
import { Form } from "@/components/ui/form/form";
import { TextInput } from "@/components/ui/form/text-input";
import { VisibilityToggle } from "../components/visibility-toggle";
import { useModalError } from "@/hooks/use-modal-error";
import { useSegment } from "@/hooks/use-segment";
type Form = z.infer<typeof schema>;

/*
 * CreateProjectModal component
 * This component is used to create a new project - we provide the project name and visibility (private or public)
 */
export const CreateProjectModal = () => {
  /* Stores */
  const [, navigate] = useLocation();

  /* Queries */
  const { mutateAsync: createProject, isPending: isCreateProjectPending, isError: isCreateProjectError } = useCreateProject();

  /* Hooks */
  const { segmentTrackEvent } = useSegment();
  const { close } = useModalError({ error: isCreateProjectError });

  /* Handlers */
  const handleCreate = async (values: Form) => {
    // if value.visibility is true, set it to "public", otherwise set it to "private"
    // this was done to match the schema of the project object and keep the usage of checkboxes consistent
    const updatedValues = { ...values };
    updatedValues.visibility = values.visibility ? "private" : "public";
    const resp = await createProject(updatedValues);

    // Segment tracking
    segmentTrackEvent("Project Created", { projectId: resp.project.id, projectName: resp.project.name });

    // Navigate to the project page
    navigate(`/projects/${resp.project.id}`);
    close();
  };

  /* Utils */
  const generateRandomThumbnailName = () => {
    // generate a random number between 1 and 30 to use as the thumbnail name
    // this is because we have 30 different project thumbnails each named project-1 to project-30
    const randomNumber = Math.floor(Math.random() * 30) + 1;
    return `project-${randomNumber}`;
  };

  /* Render */
  return (
    <Form onSubmit={handleCreate} schema={schema}>
      {({ register, formState: { errors, isValid } }) => (
        <>
          {/* Header */}
          <div className="flex items-center justify-between pb-4">
            <h2>Project name</h2>
            <XMarkIcon onClick={close} className="size-5 cursor-pointer" />
          </div>

          {/* Details */}
          <TextInput
            label="Project title"
            placeholder="For example 'Promotion Campaign Spring 2025 Studies'"
            error={errors.name?.message}
            registration={register("name")}
            className="mb-4"
          />

          {/* Visibility toggle */}
          <VisibilityToggle checked={false} register={register} />

          {/* Thumbnail name */}
          <TextInput type="hidden" registration={register("thumbnailName")} value={generateRandomThumbnailName()} />

          {/* Buttons */}
          <div className="flex justify-end pt-4">
            <Button type="submit" variant="primary" disabled={isCreateProjectPending || !isValid}>
              {isCreateProjectPending ? "Creating..." : "Continue"}
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};
